import { Add } from "iconsax-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CMSFilterBar } from "../components/CMSFilterBar";
import { CMSPagination } from "../components/CMSPagination";
import { CMSTable } from "../components/CMSTable";
import { CMSSheet } from "../components/CMSSheet";
import { TestimonialForm } from "../components/TestimonialForm";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { DateFilterModal } from "../components/DateFilterModal";
import instance from "../../../../axios/instance";

const statuses = ["All Status", "published", "draft", "archived"];

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [paginatedTestimonials, setPaginatedTestimonials] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const itemsPerPage = 10;
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    testimonial: null,
  });
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [formData, setFormData] = useState({
    title: "",
    patient: "",
    category: "",
    videoUrl: "",
    image: "",
    description: "",
    featured: false,
  });

  const token = window.sessionStorage.getItem("ACCESS_TOKEN");

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await instance.get("/doctor/cms/testimonials", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestimonials(response.data);
      setFilteredTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      toast.error("Failed to load testimonials");
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = testimonials;

    if (searchQuery) {
      filtered = filtered.filter((testimonial) =>
        testimonial.title.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    if (selectedStatus !== "All Status") {
      filtered = filtered.filter(
        (testimonial) => testimonial.status === selectedStatus,
      );
    }

    if (dateRange.startDate && dateRange.endDate) {
      filtered = filtered.filter((testimonial) => {
        const testimonialDate = new Date(testimonial.created_at);
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        return testimonialDate >= start && testimonialDate <= end;
      });
    }

    setFilteredTestimonials(filtered);
    setPageIndex(1);
  }, [searchQuery, selectedStatus, dateRange, testimonials]);

  useEffect(() => {
    const startIndex = (pageIndex - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedTestimonials(filteredTestimonials.slice(startIndex, endIndex));
  }, [filteredTestimonials, pageIndex]);

  const handleAction = async (action, testimonial) => {
    setOpenDropdown(null);

    switch (action) {
      case "publish":
        await handleStatusChange(testimonial.id, "published");
        break;
      case "unpublish":
        await handleStatusChange(testimonial.id, "draft");
        break;
      case "edit":
        handleEditTestimonial(testimonial);
        break;
      case "delete":
        setDeleteModal({ isOpen: true, testimonial });
        break;
      default:
        break;
    }
  };

  const handleStatusChange = async (id, status) => {
    setLoading(true);
    try {
      await instance.put(
        `/doctor/cms/testimonials/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchTestimonials();
      toast.success(`Testimonial status updated to ${status}`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update testimonial status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = async (id, currentFeatured) => {
    setLoading(true);
    try {
      await instance.put(
        `/doctor/cms/testimonials/${id}`,
        { featured: !currentFeatured },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchTestimonials();
      toast.success(
        currentFeatured ? "Removed from featured" : "Added to featured",
      );
    } catch (error) {
      console.error("Error toggling featured:", error);
      toast.error("Failed to toggle featured status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      title: "",
      patient: "",
      category: "",
      videoUrl: "",
      image: "",
      description: "",
      featured: false,
    });
    setIsSheetOpen(true);
  };

  const handleEditTestimonial = async (testimonial) => {
    setLoading(true);
    try {
      const response = await instance.get(
        `/doctor/cms/testimonials/${testimonial.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const testimonialData = response.data;
      setIsEditing(true);
      setEditingId(testimonial.id);
      setFormData({
        title: testimonialData.title || "",
        patient: testimonialData.patient_id || "",
        category: testimonialData.product_id || "",
        videoUrl: testimonialData.video_url || "",
        image: testimonialData.image_url || "",
        description: testimonialData.description || "",
        featured: testimonialData.featured || false,
      });
      setIsSheetOpen(true);
    } catch (error) {
      console.error("Error fetching testimonial:", error);
      toast.error("Failed to load testimonial. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleSaveTestimonial = async () => {
    setLoading(true);

    try {
      const dataToSend = {
        title: formData.title,
        patient_id: formData.patient,
        product_id: formData.category || null,
        video_url: formData.videoUrl,
        image_url: formData.image || null,
        description: formData.description,
        featured: formData.featured,
      };

      if (isEditing) {
        await instance.put(
          `/doctor/cms/testimonials/${editingId}`,
          dataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        await instance.post("/doctor/cms/testimonials", dataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      setFormData({
        title: "",
        patient: "",
        category: "",
        videoUrl: "",
        image: "",
        description: "",
        featured: false,
      });

      setIsSheetOpen(false);
      setIsEditing(false);
      setEditingId(null);
      await fetchTestimonials();
      toast.success(
        isEditing
          ? "Testimonial updated successfully!"
          : "Testimonial created successfully!",
      );
    } catch (error) {
      console.error("Error saving testimonial:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to save testimonial. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);

    try {
      const testimonial = deleteModal.testimonial;

      if (testimonial.status === "archived") {
        await instance.delete(
          `/doctor/cms/testimonials/${testimonial.id}/permanent`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } else {
        await instance.delete(
          `/doctor/cms/testimonials/${testimonial.id}/soft`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }

      setDeleteModal({ isOpen: false, testimonial: null });
      await fetchTestimonials();
      toast.success(
        testimonial.status === "archived"
          ? "Testimonial permanently deleted!"
          : "Testimonial archived successfully!",
      );
    } catch (error) {
      console.error("Error deleting testimonial:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to delete testimonial. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.title &&
    formData.patient &&
    formData.category &&
    (formData.videoUrl || formData.image) &&
    formData.description;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const columns = [
    { key: "title", label: "Title", align: "left" },
    { key: "patient", label: "Patient", align: "left" },
    { key: "product", label: "Category", align: "left" },
    { key: "featured", label: "Featured", align: "left" },
    { key: "status", label: "Status", align: "left" },
    { key: "updated", label: "Updated", align: "left" },
    { key: "actions", label: "", align: "center" },
  ];

  const actions = [
    {
      key: "publish",
      label: "Publish",
      variant: "success",
      condition: (item) => item.status !== "published",
    },
    {
      key: "unpublish",
      label: "Unpublish",
      variant: "warning",
      condition: (item) => item.status === "published",
    },
    {
      key: "edit",
      label: "Edit",
      variant: "primary",
    },
    {
      key: "delete",
      label: (testimonial) =>
        testimonial.status === "archived" ? "Delete Permanently" : "Archive",
      variant: "danger",
    },
  ];

  const renderCell = (key, item) => {
    switch (key) {
      case "title":
        return (
          <div className="text-[14px] font-medium text-gray-900">
            {item.title}
          </div>
        );
      case "patient":
        return (
          <div className="text-[14px] text-gray-600">
            {item.patient_name || "-"}
          </div>
        );
      case "product":
        return (
          <div className="text-[14px] text-gray-600">
            {item.product_name || "-"}
          </div>
        );
      case "featured":
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={item.featured || false}
              onChange={() => handleToggleFeatured(item.id, item.featured)}
              disabled={loading}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        );
      case "status":
        return (
          <span
            className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium capitalize ${
              item.status === "published"
                ? "bg-green-100 text-green-800"
                : item.status === "draft"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
            }`}
          >
            {item.status}
          </span>
        );
      case "updated":
        return (
          <div className="text-[14px] text-gray-600">
            {formatDate(item.updated_at || item.created_at)}
          </div>
        );
      default:
        return <div className="text-[14px] text-gray-900">{item[key]}</div>;
    }
  };

  return (
    <div className="bg-[#fafafa] min-h-screen">
      <div className="max-w-[100%] w-full px-4 lg:px-7 py-5">
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-2">
            <div className="mb-2 lg:mb-0">
              <h1 className="text-2xl font-bold text-gray-900">Testimonials</h1>
              <p className="text-gray-600 text-[14px] mt-1 max-w-2xl">
                Manage patient testimonials and reviews.
              </p>
            </div>
            <button
              onClick={handleOpenSheet}
              className="bg-primary py-2.5 px-4 rounded-md font-medium text-[14px] hover:opacity-80 text-white flex items-center gap-2 transition-all duration-300"
            >
              <Add size={18} />
              New Testimonial
            </button>
          </div>
        </div>

        <CMSFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search testimonials..."
          statuses={statuses}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          showDateFilter={true}
          onDateClick={() => setIsDateFilterOpen(true)}
        />

        <CMSTable
          columns={columns}
          data={paginatedTestimonials}
          loading={loading}
          emptyMessage="No testimonials found"
          onActionClick={handleAction}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          actions={actions}
          renderCell={renderCell}
        />

        {!loading && (
          <CMSPagination
            currentPage={pageIndex}
            totalItems={filteredTestimonials.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setPageIndex}
            itemName="testimonials"
            showingCount={paginatedTestimonials.length}
          />
        )}
      </div>

      <CMSSheet
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        onCancel={handleCloseSheet}
        onSave={handleSaveTestimonial}
        title={isEditing ? "Edit Testimonial" : "New Testimonial"}
        desc={"Manage patient testimonials and reviews."}
        saveButtonText={isEditing ? "Update" : "Create"}
        cancelButtonText="Cancel"
        isLoading={loading}
        isSaveDisabled={!isFormValid}
      >
        <TestimonialForm formData={formData} onChange={setFormData} />
      </CMSSheet>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, testimonial: null })}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.testimonial?.title}
        isLoading={loading}
        message={
          deleteModal.testimonial?.status === "archived"
            ? "This will permanently delete the testimonial. This action cannot be undone."
            : "This will archive the testimonial. You can permanently delete it later from the archived items."
        }
      />

      <DateFilterModal
        isOpen={isDateFilterOpen}
        onClose={() => setIsDateFilterOpen(false)}
        onApply={() => setIsDateFilterOpen(false)}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />
    </div>
  );
}
